import { Button, ConfigProvider, Flex, Modal, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { showConfirmModal } from "components/modals/confirmModal";
import {
  ModalHeader,
  modalBlockLabelStyle,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import {
  classDateTime,
  keepTogether,
  UpcomingClassList,
  upcomingListStyle as listStyle,
  upcomingMetaStyle as metaStyle,
  upcomingRowStyle as rowStyle,
} from "components/modals/UpcomingClassList.component";
import { removalCountLabels, staffDeactivationCheckOptions } from "hooks/queries/user";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { UserType } from "shared/types/enum";

const { Text } = Typography;

/**
 * Nonaktifkan on a staff member asks the server first. A class of theirs
 * that hasn't started, booked or empty, blocks it: a deactivated instructor
 * can't keep a future class (clients could still book it). The pop-up lists
 * them one row per series, each opening its class, and offers to delete them
 * all and deactivate in one step (a second confirm states the numbers).
 * Otherwise it is the plain confirm. Started classes still waiting to be
 * marked are listed in both states as a reminder only: past classes stay
 * editable, but the instructor loses their app. The server re-checks, so a
 * class added in the meantime is still refused.
 *
 * mode "revoke_role" is the same pop-up for the Manager's switch that takes
 * the instructor ROLE away (the same rule: nobody would teach those classes);
 * only the wording and the no-block confirm differ.
 *
 * A SYSTEM ADMIN gets no lists at all (Andrew, 2026-09-26): the rows open the
 * studio's class pages, whose endpoints answer a system admin 401, which the
 * admin app turns into a sign-out; and they can't move or mark a studio's
 * classes anyway. So they get one confirm that states how many upcoming
 * classes and bookings go, and removes them on the spot.
 */
export const DeactivateStaffModal = ({
  open,
  user,
  mode = "deactivate",
  isSysAdminActor = false,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  user: { id: string; roles: string[] };
  mode?: "deactivate" | "revoke_role";
  isSysAdminActor?: boolean;
  pending: boolean;
  // removeUpcoming: delete every upcoming class first. onRefused refreshes
  // the list when the server refuses (a class was added meanwhile).
  onConfirm: (removeUpcoming: boolean, onRefused: () => void) => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery(staffDeactivationCheckOptions(user.id, open));
  const upcoming = data?.upcoming_classes ?? [];
  const unmarked = data?.unmarked_classes ?? [];
  const revoking = mode === "revoke_role";
  // Only a deactivation takes an admin account's dashboard away.
  const isAdminTier = !revoking && user.roles.includes(UserType.Admin);
  const adminNote = translate(
    `messages.warning.deactivate_admin.content_${isSysAdminActor ? "sysadmin" : "manager"}`
  );

  const openClass = (scheduleId: string, occurrence: string) => {
    onClose();
    navigate(
      `${PATHS.SCHEDULE}/${scheduleId}?occurrence_timestamp=${encodeURIComponent(occurrence)}`
    );
  };

  const unmarkedSection = !isSysAdminActor && unmarked.length > 0 && (
    <>
      <Text style={modalBlockLabelStyle}>{translate("staff_status.unmarked.label")}</Text>
      <Text style={{ ...metaStyle, display: "block", marginBottom: 8 }}>
        {translate(revoking ? "staff_status.unmarked.note_role" : "staff_status.unmarked.note")}
      </Text>
      <Flex vertical gap={8} style={listStyle}>
        {unmarked.map((c) => (
          <button
            key={`${c.schedule_id}_${c.occurrence_timestamp}`}
            type="button"
            style={rowStyle}
            onClick={() => openClass(c.schedule_id, c.occurrence_timestamp)}
          >
            <Flex vertical style={{ minWidth: 0 }}>
              <Text strong ellipsis>
                {c.class_name}
              </Text>
              <Text style={metaStyle}>
                {classDateTime(c.occurrence_timestamp)} ·{" "}
                {keepTogether(
                  translateWithVars("staff_status.unmarked.count", { n: c.unmarked_count })
                )}
              </Text>
            </Flex>
          </button>
        ))}
      </Flex>
      {data && data.unmarked_total > unmarked.length && (
        <Text style={{ ...metaStyle, display: "block", marginTop: 8 }}>
          {translateWithVars("staff_status.unmarked.more", {
            n: data.unmarked_total - unmarked.length,
          })}
        </Text>
      )}
    </>
  );

  const confirmRemoval = () => {
    if (!data) return;
    const { classes, bookings } = data.totals;
    showConfirmModal({
      tone: "error",
      title: translate(revoking ? "staff_status.role.remove_title" : "staff_status.remove.title"),
      content: (
        <>
          <Text style={modalSubtitleStyle}>
            {translateWithVars(
              `staff_status.${revoking ? "role.remove_" : "remove."}${
                bookings > 0 ? "body_bookings" : "body"
              }`,
              removalCountLabels(classes, bookings)
            )}
          </Text>
          {isAdminTier && <Text style={modalSubtitleStyle}>{adminNote}</Text>}
        </>
      ),
      confirmLabel: translate(
        revoking ? "staff_status.role.remove_confirm" : "staff_status.remove.confirm"
      ),
      destructive: true,
      onConfirm: () => onConfirm(true, () => refetch()),
    });
  };

  const body = () => {
    if (isLoading || !data) {
      return (
        <Flex justify="center" style={{ padding: "28px 0" }}>
          <Spin />
        </Flex>
      );
    }
    if (upcoming.length > 0 && isSysAdminActor) {
      const { classes, bookings } = data.totals;
      return (
        <>
          <ModalHeader
            tone="error"
            title={translate(
              revoking
                ? "staff_roles.confirm_revoke_title"
                : "messages.warning.deactivate_admin.title"
            )}
          />
          <Text style={modalSubtitleStyle}>
            {translateWithVars(
              bookings > 0 ? "staff_status.summary.body_bookings" : "staff_status.summary.body",
              removalCountLabels(classes, bookings)
            )}
          </Text>
          {isAdminTier && <Text style={modalSubtitleStyle}>{adminNote}</Text>}
          {!data.can_remove_all && (
            <Text style={{ ...metaStyle, display: "block", marginTop: 12 }}>
              {translate("messages.error.too_many_upcoming_classes")}
            </Text>
          )}
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" gap={10} wrap="wrap" style={{ marginTop: 22 }}>
              <Button onClick={onClose} disabled={pending}>
                {translate("button.cancel")}
              </Button>
              <Button
                type="primary"
                danger
                loading={pending}
                disabled={!data.can_remove_all}
                onClick={() => onConfirm(true, () => refetch())}
              >
                {translate(
                  revoking ? "staff_status.role.remove_button" : "staff_status.remove.button"
                )}
              </Button>
            </Flex>
          </ConfigProvider>
        </>
      );
    }
    if (upcoming.length > 0) {
      return (
        <>
          <ModalHeader
            tone="amber"
            title={translate(
              revoking ? "staff_status.role.blocked_title" : "staff_status.deactivate.blocked_title"
            )}
          />
          <Text style={modalSubtitleStyle}>
            {translate(
              revoking ? "staff_status.role.blocked_body" : "staff_status.deactivate.blocked_body"
            )}
          </Text>
          <Text style={modalBlockLabelStyle}>{translate("staff_status.upcoming.label")}</Text>
          <UpcomingClassList rows={upcoming} variant="instructor" onOpen={openClass} />
          {unmarkedSection}
          {!data.can_remove_all && (
            <Text style={{ ...metaStyle, display: "block", marginTop: 12 }}>
              {translate("messages.error.too_many_upcoming_classes")}
            </Text>
          )}
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" gap={10} wrap="wrap" style={{ marginTop: 22 }}>
              <Button onClick={onClose} disabled={pending}>
                {translate("button.close")}
              </Button>
              <Button
                type="primary"
                danger
                loading={pending}
                disabled={!data.can_remove_all}
                onClick={confirmRemoval}
              >
                {translate(
                  revoking ? "staff_status.role.remove_button" : "staff_status.remove.button"
                )}
              </Button>
            </Flex>
          </ConfigProvider>
        </>
      );
    }
    // Nothing ahead: the role switch keeps its own one-line confirm (amber,
    // plain Konfirmasi), a deactivation its red one.
    if (revoking) {
      return (
        <>
          <ModalHeader tone="amber" title={translate("staff_roles.confirm_revoke_title")} />
          <Text style={modalSubtitleStyle}>{translate("staff_roles.confirm_revoke_body")}</Text>
          {unmarkedSection}
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
              <Button onClick={onClose} disabled={pending}>
                {translate("button.cancel")}
              </Button>
              <Button
                type="primary"
                loading={pending}
                onClick={() => onConfirm(false, () => refetch())}
              >
                {translate("button.confirm")}
              </Button>
            </Flex>
          </ConfigProvider>
        </>
      );
    }
    const isInstructor = user.roles.includes(UserType.Instructor);
    return (
      <>
        <ModalHeader
          tone="error"
          title={
            isAdminTier
              ? translate("messages.warning.deactivate_admin.title")
              : translate(
                  isInstructor
                    ? "staff_status.deactivate.title_instructor"
                    : "staff_status.deactivate.title_staff"
                )
          }
        />
        <Text style={modalSubtitleStyle}>
          {isAdminTier
            ? adminNote
            : translate(
                isInstructor
                  ? "staff_status.deactivate.body_instructor"
                  : "staff_status.deactivate.body_staff"
              )}
        </Text>
        {unmarkedSection}
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={onClose} disabled={pending}>
              {translate("button.cancel")}
            </Button>
            <Button
              type="primary"
              danger
              loading={pending}
              onClick={() => onConfirm(false, () => refetch())}
            >
              {translate("button.deactivate_user")}
            </Button>
          </Flex>
        </ConfigProvider>
      </>
    );
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable
      maskClosable={!pending}
      width={480}
      onCancel={onClose}
      destroyOnClose
    >
      {body()}
    </Modal>
  );
};
